import { apiClient, getToken } from '../api/apiClient';

/**
 * TTS Voice Configuration (matches backend TtsVoiceConfigDto)
 */
export interface TtsVoiceConfig {
  voiceId: string;
  displayName: string;
  gender: string;
  description: string;
  isEnabled: boolean;
  isDefault: boolean;
}

/**
 * TTS Audio Chunk DTO (matches backend)
 */
export interface TtsAudioChunkDto {
  id: string;
  bookId: string;
  bookPageTextId: string;
  chunkIndex: number;
  startCharOffset: number;
  endCharOffset: number;
  textContent?: string;
  voiceGender: string; // Kept for backward compatibility (gender label)
  ttsProvider: string;
  voiceId: string;
  audioBlobUrl?: string;
  audioSizeBytes?: number;
  durationSeconds?: number;
  generatedAt?: string;
}

/**
 * Request to generate audio chunk
 */
export interface GenerateAudioChunkRequest {
  bookPageTextId: string;
  chunkIndex: number;
  voiceId: string; // Changed from voiceGender
}

/**
 * BookPageText DTO (for Web Speech API fallback)
 */
export interface BookPageTextDto {
  id: string;
  bookId: string;
  pageNumber: number;
  plainText: string;
  wordCount: number;
  contentType: string;
  chapterTitle?: string;
  chunkIndex?: number;
}

/**
 * TTS service for interacting with backend TTS APIs
 */
class TtsService {
  // Request deduplication cache: Map<requestKey, Promise>
  private pendingRequests = new Map<string, Promise<TtsAudioChunkDto>>();

  /**
   * Get BookPageText ID by book ID and location (chapter index or page number).
   * Returns null if not found.
   */
  async getBookPageTextId(
    bookId: string,
    chapterIndex?: number,
    pageNumber?: number
  ): Promise<string | null> {
    const response = await apiClient.get<string | null>(
      `/api/app/book-page-texts/id-by-location`,
      {
        params: {
          bookId,
          chapterIndex,
          pageNumber
        }
      }
    );
    return response.data;
  }

  /**
   * Get BookPageText entity by ID (for Web Speech API fallback).
   * Returns the full BookPageText with plain text content.
   */
  async getBookPageText(bookPageTextId: string): Promise<BookPageTextDto> {
    const response = await apiClient.get<BookPageTextDto>(
      `/api/app/book-page-texts/${bookPageTextId}`
    );
    return response.data;
  }

  /**
   * Get total number of chunks for a BookPageText.
   * Used to show progress (e.g., "Playing chunk 3 of 12").
   */
  async getTotalChunks(bookPageTextId: string): Promise<number> {
    const response = await apiClient.get<number>(
      `/api/app/tts-audio-chunks/total-chunks/${bookPageTextId}`
    );
    return response.data;
  }

  /**
   * Get available voices for students (only enabled voices).
   * Used to populate the voice selection dropdown in TTS settings.
   */
  async getAvailableVoices(): Promise<TtsVoiceConfig[]> {
    const response = await apiClient.get<TtsVoiceConfig[]>(
      '/api/app/tts-audio-chunks/available-voices'
    );
    return response.data;
  }

  /**
   * Get all existing audio chunks for a BookPageText.
   * Used to check if chunks are already generated (cache lookup).
   * @deprecated Use voiceId parameter instead of voiceGender
   */
  async getChunksByBookPageText(
    bookPageTextId: string,
    voiceGender: string
  ): Promise<TtsAudioChunkDto[]> {
    const response = await apiClient.get<TtsAudioChunkDto[]>(
      `/api/app/tts-audio-chunks/by-page-text/${bookPageTextId}`,
      {
        params: { voiceGender }
      }
    );
    return response.data;
  }

  /**
   * Generate audio for a specific chunk on-demand.
   * Returns cached audio if already generated, otherwise generates new audio.
   * Implements request deduplication to prevent multiple simultaneous calls for the same chunk.
   */
  async generateChunk(request: GenerateAudioChunkRequest): Promise<TtsAudioChunkDto> {
    // Create unique key for this request
    const requestKey = `${request.bookPageTextId}-${request.chunkIndex}-${request.voiceId}`;

    // If there's already a pending request for this chunk, return that promise
    const existingRequest = this.pendingRequests.get(requestKey);
    if (existingRequest) {
      console.log(`[TTS] Reusing pending request for chunk ${request.chunkIndex}`);
      return existingRequest;
    }

    // Create new request promise
    const requestPromise = apiClient.post<TtsAudioChunkDto>(
      '/api/app/tts-audio-chunks/generate',
      request
    ).then(response => {
      // Remove from pending requests when complete
      this.pendingRequests.delete(requestKey);
      return response.data;
    }).catch(error => {
      // Remove from pending requests on error too
      this.pendingRequests.delete(requestKey);
      throw error;
    });

    // Store in pending requests
    this.pendingRequests.set(requestKey, requestPromise);
    console.log(`[TTS] New request for chunk ${request.chunkIndex}`);

    return requestPromise;
  }

  /**
   * Get audio stream URL for a specific chunk.
   * Returns URL that can be used in <audio> element.
   */
  getAudioStreamUrl(ttsAudioChunkId: string): string {
    // Include authentication token in URL for audio streaming
    const token = getToken();
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://localhost:44306';
    return `${baseUrl}/api/app/tts-audio-chunks/stream/${ttsAudioChunkId}?access_token=${token}`;
  }

  /**
   * Prefetch and generate multiple chunks ahead for seamless playback.
   * Generates chunks in background to avoid playback interruptions.
   */
  async prefetchChunks(
    bookPageTextId: string,
    startChunkIndex: number,
    count: number,
    voiceId: string
  ): Promise<TtsAudioChunkDto[]> {
    const totalChunks = await this.getTotalChunks(bookPageTextId);
    const endChunkIndex = Math.min(startChunkIndex + count, totalChunks);

    const prefetchPromises: Promise<TtsAudioChunkDto>[] = [];
    for (let i = startChunkIndex; i < endChunkIndex; i++) {
      prefetchPromises.push(
        this.generateChunk({
          bookPageTextId,
          chunkIndex: i,
          voiceId
        })
      );
    }

    return Promise.all(prefetchPromises);
  }
}

export const ttsService = new TtsService();
